import React, { Dispatch, useEffect, useState } from 'react'
import { connect, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { ComponentPropsGetterRC } from 'react-table'

import { isEmpty } from 'lodash-es'
import { AnyAction, bindActionCreators } from 'redux'

import { ProviderAdminProviderInfo } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import PrimaryButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/primaryButton/PrimaryButton'
import LoadingIndicator from '@lyrahealth-inc/ui-core/lib/atoms/loadingIndicator/LoadingIndicator'
import ContentLayout from '@lyrahealth-inc/ui-core/lib/molecules/layouts/base/ContentLayout'
import BootstrapContainer from '@lyrahealth-inc/ui-core/lib/molecules/layouts/bootstrapContainer/BootstrapContainer'
import DataTable from '@lyrahealth-inc/ui-core/lib/molecules/table/DataTable'
import { InputField } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/InputField'
import { CheckIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/CheckIcon'
import { DownloadFileIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/DownloadFileIcon'
import { ExclamationIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ExclamationIcon'
import { SearchIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SearchIcon'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import styles from './providersDashboard.module.scss'
import { ROLES } from '../../common/constants/appConstants'
import { GET_PROVIDERS_LITE } from '../../common/constants/reduxConstants'
import { PROVIDERS_DETAILS_INFO } from '../../common/constants/routingConstants'
import { hasErrorForAction, hasRole } from '../../common/utils/utils'
import { getAlertsState } from '../../data/alertsSelectors'
import { Alert } from '../../data/alertTypes'
import { getAuthRoles } from '../../data/auth/authSelectors'
import { RootState } from '../../data/store'
import * as providersDataActions from '../data/providersDataActions'
import { getProvidersIsDownloadingCSV, getProvidersList } from '../data/providerSelectors'
import * as providerDetailsActions from '../individualProvider/data/providerDetailsActions'

let tableColumns: Array<{
  Header: string
  accessor: string
  Cell?: any
  minWidth?: number | undefined
  maxWidth?: number | undefined
}> = [
  {
    Header: 'Name',
    accessor: 'full_name',
  },
  {
    Header: 'Email',
    accessor: 'email',
    minWidth: 300,
  },
]

const ProvidersDashboard: React.FC<ProviderDashboardProps> = ({
  providersList,
  isDownloadingCSV,
  actions: { getProviders, providerSelected, downloadProviders },
}) => {
  const roles: string[] = useSelector(getAuthRoles)
  const alerts: Alert[] = useSelector(getAlertsState)
  const [search, setSearch] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    getProviders()
  }, [getProviders])

  const rowClickHandler = (state: RootState, rowInfo: any): void => {
    providerSelected(rowInfo.original)
    navigate(PROVIDERS_DETAILS_INFO.route, { state: { provider_id: rowInfo.original.lyra_id } })
  }

  if (!providersList) {
    if (hasErrorForAction(alerts, GET_PROVIDERS_LITE)) {
      return <div /> // custom workflow or display could render here
    } else {
      return (
        <ContentLayout>
          <BootstrapContainer col='col-md-12'>
            <h2 className={styles.title}>Providers</h2>
            <div className={`${styles.card} ${styles['loading-container']}`}>
              <LoadingIndicator size={45} />
            </div>
          </BootstrapContainer>
        </ContentLayout>
      )
    }
  }

  const filteredData = !search
    ? providersList
    : providersList.filter((row: any) => {
        return (
          row.full_name.toLowerCase().includes(search.toLowerCase()) ||
          row.email.toLowerCase().includes(search.toLowerCase())
        )
      })

  if (tableColumns.length === 2 && hasRole(roles, ROLES.PROVIDERS_ADMIN)) {
    tableColumns = tableColumns.concat([
      {
        Header: 'Rates Set',
        accessor: 'rates',
        maxWidth: 130,
        Cell: (row: any) => (
          <span>
            {!isEmpty(row.value) ? (
              <CheckIcon fillColor={styles.x_success} isFilled size={15} withCircle />
            ) : (
              <ExclamationIcon isFilled size={15} fillColor={styles.x_alert} />
            )}
          </span>
        ),
      },
      {
        Header: 'Calendar Status',
        accessor: 'calendar_state',
      },
    ])
  }

  return (
    <ContentLayout>
      <BootstrapContainer col='col-md-12'>
        <h2 className={styles.title}>Providers</h2>
        <div className={styles['filter-add-container']}>
          <InputField
            value={search}
            onChange={(e) => setSearch(e.nativeEvent.text)}
            baseInputStyle={{ width: 350, marginBottom: 15, flexShrink: 1 }}
            leftIcon={<SearchIcon size={15} fillColor={styles.x_light_gray_10} />}
            placeholder='Search by name or email'
            testID={tID('ProvidersDashboard-search-input')}
          />
          {hasRole(roles, ROLES.PAYMENTS_ADMIN) ? (
            <PrimaryButton
              onClick={downloadProviders}
              id='test-providersdashboard-downloadcsv'
              styleType='line'
              className={styles['csv-download']}
              isLoading={isDownloadingCSV}
            >
              <DownloadFileIcon size={20} fill='currentColor' />
              CSV
            </PrimaryButton>
          ) : (
            []
          )}
        </div>

        <DataTable
          pagination
          pageSize={20}
          destinationSelector='lyra_id'
          rowClickHandler={rowClickHandler as unknown as ComponentPropsGetterRC}
          className={styles.card}
          // @ts-expect-error TS(2740): Type 'Iterable<number, ProviderAdminProviderInfo>'... Remove this comment to see the full error message
          data={filteredData}
          columns={tableColumns}
        />
      </BootstrapContainer>
    </ContentLayout>
  )
}

type ProviderDashboardProps = {
  actions: {
    downloadProviders: () => void
    getProviders: () => void
    providerSelected: (rowInfo: any) => void
  }
  providersList: ProviderAdminProviderInfo[]
  isDownloadingCSV: boolean
}

const mapStateToProps = (state: RootState): any => {
  return {
    providersList: getProvidersList(state),
    isDownloadingCSV: getProvidersIsDownloadingCSV(state),
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => {
  return {
    // @ts-expect-error TS(2769): No overload matches this call.
    actions: bindActionCreators({ ...providerDetailsActions, ...providersDataActions }, dispatch),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ProvidersDashboard)
